import { ManageInvestorDetailsContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/InvestorDetails';
import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { postMipInvestorsReleaseSingle } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import {
	CHIP_THEME,
	Chips as ChipsV2,
	COLOR,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { DashCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/DashCard/DashCard';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import React, { useContext, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import {
	InvestmentWorkflowStatus,
	OutstandingDocumentForReleaseRes,
} from '@dealstack/shared/lib/generated';

interface Props {
	className?: string;
	investmentStatus: InvestmentWorkflowStatus;
	/** When false, blocks release due to outstanding documents. */
	canRelease?: boolean;
	outstandingDocumentsForRelease?: OutstandingDocumentForReleaseRes[];
}

export const DashcardInvestments = ({
	className,
	investmentStatus,
	canRelease,
	outstandingDocumentsForRelease,
}: Props) => {
	const { mipId } = useRequiredParams('mipId');
	const manageInvestorDetailsContext = React.useContext(ManageInvestorDetailsContext);
	const { investorId, refreshMIPInvestorDetailsData } = manageInvestorDetailsContext;
	const postMipInvestorsReleaseSingleRequest = useMutation({
		mutationFn: postMipInvestorsReleaseSingle,
	});
	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();
	const [releaseButtonDisabled, setReleaseButtonDisabled] = useState<boolean>(false);

	const MIPManageContextV2 = useContext(MIPManageInvestorsV2Context);
	// Backwards compatible: when canRelease is undefined (flag off), show Release when status is PENDING_RELEASE
	const showReleaseButton =
		investmentStatus === InvestmentWorkflowStatus.COMPLETE &&
		(canRelease === undefined || canRelease === true);
	const isBlockedByDocuments =
		investmentStatus === InvestmentWorkflowStatus.COMPLETE &&
		canRelease === false &&
		(outstandingDocumentsForRelease?.length ?? 0) > 0;

	const onReleaseOnInvestorDetailsPage = () => {
		if (!investorId) {
			return;
		}
		setReleaseButtonDisabled(true);
		postMipInvestorsReleaseSingleRequest.mutate(
			{ mipId, investorId },
			{
				onSuccess: () => {
					showToastV2({
						title: tMipManage('send_modal.toast.success.message_2'),
					});
					refreshMIPInvestorDetailsData();
					MIPManageContextV2.refreshData();

					setReleaseButtonDisabled(false);
				},
				onError: (responseError) => {
					const errorMessage = isStandardBackendError(responseError)
						? tDefault(`error_codes.${responseError.error?.code}`)
						: tMipManage('send_modal.toast.error.message');

					showToastV2({
						title: errorMessage,
						state: 'error',
					});

					setReleaseButtonDisabled(false);

					throw responseError;
				},
			}
		);
	};

	const getStatusChip = () => {
		const statusMap: Partial<Record<InvestmentWorkflowStatus, string>> = {
			[InvestmentWorkflowStatus.OPEN]: tMipManage('investors.investment_status.draft'),
			[InvestmentWorkflowStatus.DEFINITION_COMPLETE]: tMipManage(
				'investors.investment_status.approved'
			),
			[InvestmentWorkflowStatus.IN_PROGRESS]: tMipManage(
				'investors.investment_status.in_progress'
			),
			[InvestmentWorkflowStatus.COMPLETE]: tMipManage('investors.investment_status.complete'),
			[InvestmentWorkflowStatus.RELEASED]: tMipManage('investors.investment_status.released'),
		};

		const statusText = statusMap[investmentStatus];
		if (!statusText) {
			return null;
		}

		return (
			<ChipsV2 size="s" chipTheme={CHIP_THEME.DARK} color={COLOR.GREY}>
				{statusText}
			</ChipsV2>
		);
	};

	return (
		<DashCard className={className}>
			<DashCard.Top textVariant="sans/14/600" icon="money-v3">
				{tMipManage('investors.dashcards.investments_status')}
			</DashCard.Top>
			<DashCard.Divider />
			<DashCard.Middle
				status={getStatusChip()}
				button={
					showReleaseButton ? (
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_DASHCARD_INVESTMENTS_RELEASE_BUTTON
							}
						>
							<ButtonV2
								size="xs"
								onClick={onReleaseOnInvestorDetailsPage}
								variant="primary"
								icon="flag"
								leftIcon
								buttonTheme="light"
								disabled={releaseButtonDisabled || isBlockedByDocuments}
							>
								{tMipManage('investors.actions.release')}
							</ButtonV2>
						</UIElementIdentifier>
					) : null
				}
			/>
		</DashCard>
	);
};
